import { useState } from 'react'
import { ChevronDown, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Service } from '@/lib/dashboard-data'
import type { IncidentRow } from '@/lib/api/deep-dive.types'
import { usePlatformIncidents } from '@/hooks/useDeepDive'
import { formatDateTime } from '@/lib/ws-status'
import {
  CATEGORIES,
  EVIDENCE_SOURCE,
  INCIDENT_WINDOWS,
  groupIncidents,
  httpMeaning,
  incidentCategory,
  incidentDuration,
  incidentHttpStatus,
  plainCause,
  vendorLinks,
  windowLabel,
  type IncidentGroup,
  type IncidentGrouping,
  type IncidentWindow,
} from '@/lib/platform-incidents'
import { SegmentedPicker } from './SegmentedPicker'
import { PanelEmpty, PanelLoading } from './PanelState'

/** Rows a group shows before "Show all". */
const GROUP_PREVIEW = 10

const fullTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' })

interface PlatformIncidentsLogProps {
  /** The platforms to report on; only `systemId` and `name` are read. */
  platforms: Service[]
}

/**
 * Every incident across a set of catalog platforms over a window, grouped by
 * issue type or by platform, newest first inside each group. A row reads as a
 * sentence; opening it shows the codes and the evidence behind that sentence.
 */
export function PlatformIncidentsLog({ platforms }: PlatformIncidentsLogProps) {
  const [period, setPeriod] = useState<IncidentWindow>('7d')
  const [grouping, setGrouping] = useState<IncidentGrouping>('issue')
  const keys = platforms.map((p) => p.systemId as string)
  const names = new Map(platforms.map((p) => [p.systemId as string, p.name]))
  const nameOf = (service: string) => names.get(service) ?? service
  const results = usePlatformIncidents(keys, period)

  const rows = results.flatMap((r) => r.data?.incidents ?? [])
  const total = results.reduce((n, r) => n + (r.data?.total ?? 0), 0)
  const affected = results.filter((r) => (r.data?.total ?? 0) > 0).length
  const ongoing = rows.filter((r) => r.ongoing).length
  const failed = keys.filter((_, i) => results[i]?.isError).map(nameOf)
  const capped = keys.flatMap((key, i) => {
    const page = results[i]?.data
    return page && page.total > page.incidents.length
      ? [`${nameOf(key)} (newest ${page.incidents.length} of ${page.total})`]
      : []
  })
  const groups = groupIncidents(rows, grouping, nameOf)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-fg-muted text-sm">
          <span className="text-fg font-mono font-semibold">{total}</span> incident
          {total === 1 ? '' : 's'} in the {windowLabel(period)} across{' '}
          <span className="text-fg font-mono font-semibold">{affected}</span> platform
          {affected === 1 ? '' : 's'}
          {ongoing > 0 && <span className="text-critical-bright"> · {ongoing} happening now</span>}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedPicker
            value={grouping}
            onChange={setGrouping}
            options={['issue', 'platform'] as const}
            label="Group by"
          />
          <SegmentedPicker value={period} onChange={setPeriod} options={INCIDENT_WINDOWS} />
        </div>
      </div>

      {failed.length > 0 && (
        <p className="text-degraded text-xs">Couldn't load incidents for {failed.join(', ')}.</p>
      )}
      {capped.length > 0 && (
        <p className="text-fg-subtle text-xs">Showing only the newest for {capped.join(', ')}.</p>
      )}

      {results.some((r) => r.isLoading) ? (
        <PanelLoading label="Loading incidents" />
      ) : groups.length === 0 ? (
        <PanelEmpty label={`No incidents in the ${windowLabel(period)}.`} />
      ) : (
        groups.map((group) => (
          <IncidentGroupSection key={`${grouping}-${group.key}`} group={group} nameOf={nameOf} />
        ))
      )}
    </div>
  )
}

function IncidentGroupSection({
  group,
  nameOf,
}: {
  group: IncidentGroup
  nameOf: (service: string) => string
}) {
  const [showAll, setShowAll] = useState(false)
  const visible = showAll ? group.rows : group.rows.slice(0, GROUP_PREVIEW)

  return (
    <section className="space-y-2">
      <header className="flex items-start gap-2">
        <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', group.dot)} />
        <div className="min-w-0">
          <h3 className="text-fg text-sm font-semibold">
            {group.label}{' '}
            <span className="text-fg-subtle font-mono text-xs font-normal">
              {group.rows.length}
            </span>
          </h3>
          <p className="text-fg-muted text-xs">{group.explain}</p>
        </div>
      </header>
      <ul className="space-y-1.5">
        {visible.map((row) => (
          <IncidentLine key={row.id} row={row} platform={nameOf(row.service)} />
        ))}
      </ul>
      {group.rows.length > GROUP_PREVIEW && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="text-primary-bright font-mono text-[11px] hover:opacity-80"
        >
          {showAll ? 'Show fewer' : `Show all ${group.rows.length}`}
        </button>
      )}
    </section>
  )
}

function IncidentLine({ row, platform }: { row: IncidentRow; platform: string }) {
  const [open, setOpen] = useState(false)
  const category = CATEGORIES[incidentCategory(row)]
  const code = incidentHttpStatus(row)

  return (
    <li className="border-line bg-surface rounded-lg border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="hover:bg-surface-3/50 flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors"
      >
        <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', category.dot)} />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-fg text-sm font-semibold">{platform}</span>
            <span className={cn('text-[11px] font-medium', category.text)}>{category.label}</span>
            {code && <span className="text-fg-subtle font-mono text-[10px]">HTTP {code}</span>}
            {row.ongoing && (
              <span className="border-critical/40 bg-critical/15 text-critical-bright rounded-full border px-1.5 font-mono text-[9px] font-bold uppercase">
                Now
              </span>
            )}
          </span>
          <span className="text-fg-muted mt-0.5 block text-xs">{plainCause(row)}</span>
        </span>
        <span className="shrink-0 text-right">
          <span className="text-fg block font-mono text-[11px]">
            {formatDateTime(row.started_at)}
          </span>
          <span className="text-fg-subtle block font-mono text-[10px]">
            {incidentDuration(row)}
          </span>
        </span>
        <ChevronDown
          className={cn(
            'text-fg-subtle mt-0.5 size-4 shrink-0 transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && <IncidentDetails row={row} code={code} />}
    </li>
  )
}

function IncidentDetails({ row, code }: { row: IncidentRow; code?: number }) {
  const facts: { label: string; value: string; mono?: boolean }[] = [
    { label: 'What this means', value: CATEGORIES[incidentCategory(row)].explain },
    { label: 'Started', value: fullTime(row.started_at) },
    { label: 'Ended', value: row.ended_at ? fullTime(row.ended_at) : 'Still open' },
    { label: 'Duration', value: incidentDuration(row) },
    { label: 'Status change', value: `${row.from_status} → ${row.to_status}` },
    { label: 'Indicator', value: row.indicator, mono: true },
  ]
  if (code) facts.push({ label: 'HTTP status', value: `${code}: ${httpMeaning(code)}` })
  if (row.cause_summary) {
    facts.push({ label: 'Technical cause', value: row.cause_summary, mono: true })
  }
  const links = vendorLinks(row)

  return (
    <div className="border-line space-y-3 border-t px-3 py-3 pl-8 text-xs">
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-[9rem_minmax(0,1fr)]">
        {facts.map(({ label, value, mono }) => (
          <div key={label} className="contents">
            <dt className="text-fg-subtle font-mono text-[10px] uppercase sm:pt-0.5">{label}</dt>
            <dd className={cn('text-fg break-words', mono && 'font-mono text-[11px]')}>{value}</dd>
          </div>
        ))}
      </dl>

      {row.evidence && row.evidence.length > 0 && (
        <div>
          <p className="text-fg-subtle mb-1 font-mono text-[10px] uppercase">Timeline</p>
          <ol className="space-y-1">
            {row.evidence.map((e, i) => (
              <li key={i} className="text-fg-muted flex flex-wrap gap-x-2">
                <span className="text-fg-subtle font-mono text-[10px]">{formatDateTime(e.at)}</span>
                <span className="text-fg">{EVIDENCE_SOURCE[e.source] ?? e.source}</span>
                <span className="min-w-0 break-words">{e.detail}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {links.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {links.map((url) => (
            <a
              key={url}
              href={url}
              target="_blank"
              rel="noreferrer"
              className="text-primary-bright inline-flex items-center gap-1 hover:opacity-80"
            >
              Vendor's incident page <ExternalLink className="size-3" />
            </a>
          ))}
        </div>
      )}

      <p className="text-fg-subtle font-mono text-[10px]">Incident #{row.id}</p>
    </div>
  )
}
